import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Mail, Trash2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { value: "imap_pec", label: "PEC (IMAP/SMTP)", host: "mbox.cert.legalmail.it", port: 993 },
  { value: "microsoft_graph", label: "Microsoft 365 (Graph API)", host: "graph.microsoft.com", port: 443 },
  { value: "google_workspace", label: "Google Workspace (Gmail API)", host: "imap.gmail.com", port: 993 },
];

export default function EmailInboundConfig() {
  const [configs, setConfigs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", provider: "imap_pec", host: "", port: 993, username: "", password: "", use_ssl: true, enabled: true });

  const load = async () => { try { const { data } = await api.get("/admin/email-config"); setConfigs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.label.trim() || !form.username.trim()) { toast.error("Etichetta e utente richiesti"); return; }
    try {
      await api.put(`/admin/email-config/${encodeURIComponent(form.label)}`, form);
      toast.success("Account email salvato");
      setOpen(false); setForm({ label: "", provider: "imap_pec", host: "", port: 993, username: "", password: "", use_ssl: true, enabled: true });
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (label) => { try { await api.delete(`/admin/email-config/${encodeURIComponent(label)}`); load(); } catch {} };

  return (
    <Card className="p-6 space-y-4" data-testid="email-config-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading font-semibold text-navy-900 flex items-center gap-2"><Mail className="w-4 h-4" /> Account email in ingresso</div>
          <p className="text-xs text-slate-500 mt-1">Configura caselle PEC, Outlook e Gmail. Le email vengono routate automaticamente con Smart Routing AI.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-navy-800 hover:bg-navy-900" data-testid="add-email-config-btn"><Plus className="w-4 h-4 mr-1" />Aggiungi account</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nuovo account email</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Etichetta *</Label>
                <Input value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} placeholder="Es. PEC principale" data-testid="email-label"/>
              </div>
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Select value={form.provider} onValueChange={(v) => { const p = PROVIDERS.find(x=>x.value===v); setForm({...form, provider: v, host: p?.host || "", port: p?.port || 993 }); }}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{PROVIDERS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-1.5"><Label>Host</Label><Input value={form.host} onChange={(e)=>setForm({...form, host: e.target.value})}/></div>
                <div className="space-y-1.5"><Label>Porta</Label><Input type="number" value={form.port} onChange={(e)=>setForm({...form, port: parseInt(e.target.value) || 993})}/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Utente *</Label><Input value={form.username} onChange={(e)=>setForm({...form, username: e.target.value})} placeholder="studio@pec.it" data-testid="email-user"/></div>
                <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} placeholder="••••••"/></div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2"><Lock className="w-4 h-4" />SSL/TLS</span>
                <Switch checked={form.use_ssl} onCheckedChange={(v)=>setForm({...form, use_ssl: v})} />
              </div>
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded">
                ⚠ La sincronizzazione email reale richiederà credenziali Azure App Registration (per Microsoft 365) o accesso IMAP del provider PEC. Configurazione salvata come placeholder pronto per l'attivazione futura.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
              <Button className="bg-navy-800 hover:bg-navy-900" onClick={submit} data-testid="email-save-btn">Salva configurazione</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {configs.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Nessun account email configurato.</div>}
        {configs.map(c => (
          <div key={c.label} className="flex items-center gap-3 p-3 border border-slate-200 rounded-md" data-testid={`email-cfg-${c.label}`}>
            <Mail className="w-5 h-5 text-navy-600" />
            <div className="flex-1">
              <div className="font-medium text-navy-900 text-sm">{c.label}</div>
              <div className="text-xs text-slate-500">{c.username} · {c.host}:{c.port} {c.use_ssl && "(SSL)"}</div>
            </div>
            <Badge variant="outline" className="text-xs">{c.provider}</Badge>
            {c.enabled ? <Badge className="bg-emerald-100 text-emerald-700 border-0">attivo</Badge> : <Badge variant="outline">sospeso</Badge>}
            <button onClick={()=>remove(c.label)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}
